import { FieldValues, UseFormGetFieldState } from "react-hook-form";
import { SearchFilter } from "../../common/components/functional/FilterSearchField/FilterSearchField";
import { FormValues } from "./JobAdsFilters";
import { parseAutocomplete } from "../../common/helpers/filters";
import { EMOJI_REGEX } from "../../common/helpers/emoji";

type ChangeFormValuesProps = {
  fields: Record<string, any>;
  setValue: (name: string, value: FieldValues) => void;
  getFieldState: UseFormGetFieldState<FieldValues>;
};

export const changeFormValues = ({
  fields,
  setValue,
  getFieldState,
}: ChangeFormValuesProps) => {
  for (const [field, newValue] of Object.entries(fields)) {
    const fieldState = getFieldState(field);

    if (!fieldState.isDirty) {
      setValue(field, newValue);
    }
  }
};

export const htmlToPlainText = (html: string): string => {
  const blockTags = ["p", "h1", "h2", "h3"];

  const parseNode = (node: Node): string => {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent || "";
    }

    const element = node as HTMLElement;
    const tag = element.tagName.toLowerCase();

    if (tag === "a") {
      return element.getAttribute("href") || element.textContent || "";
    }

    if (tag === "ul" || tag === "ol") {
      const ordered = tag === "ol";
      const items = Array.from(element.children)
        .filter((child) => child.tagName.toLowerCase() === "li")
        .map((li, idx) => {
          const text = parseNode(li);
          return ordered ? `${idx + 1}. ${text}` : `- ${text}`;
        });

      return `${items.join("\n")}\n`;
    }

    const text = Array.from(element.childNodes)
      .map((child) => parseNode(child))
      .join("");

    if (blockTags.includes(tag)) {
      return `${text}\n`;
    }

    return text;
  };

  if (!html) {
    return "";
  }

  const doc = new DOMParser().parseFromString(html, "text/html");

  return Array.from(doc.body.childNodes)
    .map((node) => parseNode(node))
    .join("")
    .trim();
};

export const removeEmojis = (text: string): string => {
  return text.replace(EMOJI_REGEX, "");
};

export const parseFilters = (filters: SearchFilter[]): FormValues => {
  const find = (name: string) => filters.find((f) => f.name === name);

  const dateFrom = find("dateFrom");
  const dateTo = find("dateTo");
  const area = find("area");
  const contactPersonFilter = find("contactPerson");
  const locationCityFilter = find("locationCity");
  const customerCompanyNameFilter = find("customerCompanyName");
  const hasOrderFilter = find("hasOrder");
  const campaignFilter = find("campaign");
  const statusFilter = find("status");

  return {
    dateRange: [
      dateFrom?.value ? new Date(dateFrom.value) : null,
      dateTo?.value ? new Date(dateTo.value) : null,
    ],
    area: parseAutocomplete(area),
    contactPerson: parseAutocomplete(contactPersonFilter),
    locationCity: locationCityFilter?.value
      ? locationCityFilter.value.map((cityName: string) => {
          const city = locationCityFilter?.lookupValues?.find(
            (c: any) => c.id === cityName,
          );
          return { city: cityName, county: city?.name || "" };
        })
      : [],
    customerCompanyName: parseAutocomplete(customerCompanyNameFilter),
    hasOrder: hasOrderFilter?.value || "",
    campaign: parseAutocomplete(campaignFilter),
    status: parseAutocomplete(statusFilter),
  };
};
